/// <reference lib="dom" />

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { id as generateId } from "@instantdb/react";
import type { EventPageData } from "../../../src/eventPage.ts";
import type { Language } from "../../../src/texts.ts";
import { eventPageUiTexts } from "../../../src/eventPageTexts.ts";
import {
  formatEventNotFoundTitle,
  formatEventTitle,
} from "../../../src/pageTitle.ts";
import { Spinner } from "../components/Spinner.tsx";
import { EventCard, WhatsAppLogo } from "../components/EventCard.tsx";
import { AppHeader } from "../components/AppHeader.tsx";
import { api } from "../api.ts";
import { db } from "../db.ts";

declare global {
  var __EVENT_DATA__: EventPageData | null | undefined;
  var __EVENT_LANG__: Language | undefined;
}

const eventPageData = (): EventPageData | null =>
  globalThis.__EVENT_DATA__ ?? null;

const EventNotFound = ({ language = "en" }: { language?: Language }) => {
  const texts = eventPageUiTexts[language];
  return (
    <div className="glass-card max-w-lg mx-auto p-12 text-center mt-8 animate-fade-up">
      <div className="text-5xl mb-4">🕵️‍♀️</div>
      <h1 className="text-2xl font-bold mb-3">{texts.eventNotFound}</h1>
      <p className="text-gray-400 mb-8">{texts.eventNotFoundHint}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/events"
          className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold transition-all hover:scale-105"
        >
          {texts.browseEvents}
        </Link>
        <a
          href="https://wa.me/972504244206"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-purple-500/50 text-purple-300 font-semibold transition-all hover:bg-purple-500/10"
        >
          <WhatsAppLogo className="w-5 h-5 text-emerald-400" />
          {texts.chatWithAgentFomo}
        </a>
      </div>
    </div>
  );
};

export const EventPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const initialData = eventPageData();

  const isInitialMatch = Boolean(
    initialData && id && initialData.event.id === id,
  );

  const [data, setData] = useState<EventPageData | null>(
    isInitialMatch ? initialData : null,
  );
  const language: Language = "en";
  const [loading, setLoading] = useState<boolean>(!isInitialMatch);
  const [tasteLoading, setTasteLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const user = db.useAuth().user;
  const { data: userData } = db.useQuery(
    user?.id
      ? {
        $users: {
          $: { where: { id: user.id } },
          swipes: {},
        },
      }
      : null,
  );

  const currentUser = userData?.$users?.[0];
  const userSwipes = currentUser?.swipes ?? [];
  const userSwipe = id ? userSwipes.find((s) => s.eventId === id) : undefined;
  const tasteVote = userSwipe
    ? (userSwipe.direction as "like" | "dislike")
    : null;

  const [showNotifyOption, setShowNotifyOption] = useState(false);

  const showToast = (message: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(message);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const handleTasteVote = async (direction: "like" | "dislike") => {
    const texts = eventPageUiTexts[language];
    if (!user) {
      showToast(texts.signInToSave);
      navigate("/events");
      return;
    }
    if (!id) return;

    setTasteLoading(true);
    try {
      if (userSwipe) {
        if (userSwipe.direction === direction) {
          // Toggle off
          await db.transact(db.tx.swipes[userSwipe.id].delete());
          setShowNotifyOption(false);
          showToast(
            direction === "like" ? texts.saveToMyEvents : texts.passEvent,
          );
        } else {
          // Switch direction
          const newSwipeId = generateId();
          await db.transact([
            db.tx.swipes[userSwipe.id].delete(),
            db.tx.swipes[newSwipeId].create({
              eventId: id,
              direction,
              timestamp: Date.now(),
            }).link({ user: user.id }),
          ]);
          if (direction === "like") {
            setShowNotifyOption(true);
          } else {
            setShowNotifyOption(false);
          }
          showToast(
            direction === "like" ? texts.savedToMyEvents : texts.passedEvent,
          );
        }
      } else {
        const newSwipeId = generateId();
        await db.transact(
          db.tx.swipes[newSwipeId].create({
            eventId: id,
            direction,
            timestamp: Date.now(),
          }).link({ user: user.id }),
        );
        if (direction === "like") {
          setShowNotifyOption(true);
        } else {
          setShowNotifyOption(false);
        }
        showToast(
          direction === "like" ? texts.savedToMyEvents : texts.passedEvent,
        );
      }
    } catch (err) {
      console.error("Failed to record taste vote:", err);
    } finally {
      setTasteLoading(false);
    }
  };

  useEffect(() => {
    if (data && id && data.event.id === id) {
      return;
    }

    if (!id) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    api({
      endpoint: "getEventPageData",
      payload: {
        eventId: id,
        language: "en",
      },
    })
      .then((res) => {
        if (!active) return;
        setData(res);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load event page data:", err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (!loading) {
      document.title = data
        ? formatEventTitle(data.event.structured.name)
        : formatEventNotFoundTitle(language);
    }
  }, [data, loading, language]);

  const texts = eventPageUiTexts[language];

  return (
    <div
      dir="ltr"
      className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white md:flex"
    >
      <AppHeader />

      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <main className="container mx-auto px-4 py-8 max-w-2xl">
          {loading
            ? (
              <div className="flex justify-center py-24">
                <Spinner className="h-10 w-10 text-purple-500" />
              </div>
            )
            : data
            ? (
              <EventCard
                event={data.event}
                view={data.view}
                language={language}
                showTasteControls
                tasteVote={tasteVote}
                onTasteVote={handleTasteVote}
                tasteLoading={tasteLoading}
                onSignInRequired={() => {
                  showToast(texts.signInToSave);
                  navigate("/events");
                }}
              />
            )
            : <EventNotFound language={language} />}
        </main>

        {/* Floating Toast Feedback */}
        {toastMessage && (
          <div className="fixed bottom-6 inset-x-4 max-w-md mx-auto z-[9999] bg-slate-900/95 backdrop-blur-md border border-purple-500/30 text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center justify-between gap-3 animate-fade-up">
            <div className="flex items-center gap-2.5 text-sm font-semibold">
              <span className="text-purple-400">✨</span>
              <span>{toastMessage}</span>
            </div>
            <div className="flex items-center gap-2">
              {showNotifyOption && id && (
                <button
                  type="button"
                  onClick={async () => {
                    setShowNotifyOption(false);
                    try {
                      const res = await api({
                        endpoint: "shareEventWithFriends",
                        payload: { eventId: id },
                      });
                      showToast(res.message);
                    } catch (err: unknown) {
                      showToast(
                        err instanceof Error
                          ? err.message
                          : "Failed to notify friends",
                      );
                    }
                  }}
                  className="text-xs bg-purple-600 hover:bg-purple-500 text-white font-semibold px-3 py-1.5 rounded-xl transition-all shadow-md active:scale-95 shrink-0"
                >
                  {texts.notifyFriends}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setToastMessage(null);
                  setShowNotifyOption(false);
                }}
                className="text-gray-400 hover:text-white p-1 rounded-lg"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <footer className="container mx-auto px-4 py-8">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-white transition-colors">
              {texts.privacyPolicy}
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              {texts.termsAndConditions}
            </Link>
            <Link
              to="/accessibility"
              className="hover:text-white transition-colors"
            >
              {texts.accessibility}
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};
